import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Add keyboard event listeners for accessibility
    this.addEventListener('keydown', this.#handleKeyDown, {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    // Update the "More" button's aria-expanded state
    const moreButton = this.querySelector('[slot="more"] button');
    if (moreButton) {
      moreButton.ariaExpanded = (!isDefaultSlot).toString();
    }

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    // Reset the "More" button's aria-expanded state
    const moreButton = this.querySelector('[slot="more"] button');
    if (moreButton) {
      moreButton.ariaExpanded = 'false';
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle keyboard navigation for accessibility
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyDown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const activeSubmenu = this.#state.activeItem?.parentElement?.querySelector('[ref="submenu[]"]');

    switch (event.key) {
      case 'Escape':
        // Close the active submenu and return focus to the menu item
        if (this.#state.activeItem) {
          event.preventDefault();
          const menuItem = this.#state.activeItem;
          this.#deactivate(this.#state.activeItem);
          menuItem?.focus();
        }
        break;

      case 'ArrowDown':
        // If a submenu is open, navigate down within it
        if (activeSubmenu) {
          event.preventDefault();
          const links = activeSubmenu.querySelectorAll('a[href]');
          const currentIndex = Array.from(links).indexOf(/** @type {HTMLAnchorElement} */ (target));
          const nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
          /** @type {HTMLElement} */ (links[nextIndex])?.focus();
        } else if (target.matches('[ref="menuitem"]') && target.getAttribute('aria-haspopup') === 'true') {
          // Open submenu and focus first link
          event.preventDefault();
          const parentLi = target.closest('.menu-list__list-item');
          if (parentLi) {
            this.activate(event);
            setTimeout(() => {
              const submenu = parentLi.querySelector('[ref="submenu[]"]');
              const firstLink = submenu?.querySelector('a[href]');
              /** @type {HTMLElement} */ (firstLink)?.focus();
            }, 50);
          }
        }
        break;

      case 'ArrowUp':
        // Navigate up within submenu
        if (activeSubmenu) {
          event.preventDefault();
          const links = activeSubmenu.querySelectorAll('a[href]');
          const currentIndex = Array.from(links).indexOf(/** @type {HTMLAnchorElement} */ (target));
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
          /** @type {HTMLElement} */ (links[prevIndex])?.focus();
        }
        break;

      case 'ArrowRight':
        // Move to next top-level menu item
        if (target.matches('[ref="menuitem"]')) {
          event.preventDefault();
          const menuItems = this.querySelectorAll('[ref="menuitem"]');
          const currentIndex = Array.from(menuItems).indexOf(target);
          const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
          /** @type {HTMLElement} */ (menuItems[nextIndex])?.focus();
        }
        break;

      case 'ArrowLeft':
        // Move to previous top-level menu item
        if (target.matches('[ref="menuitem"]')) {
          event.preventDefault();
          const menuItems = this.querySelectorAll('[ref="menuitem"]');
          const currentIndex = Array.from(menuItems).indexOf(target);
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
          /** @type {HTMLElement} */ (menuItems[prevIndex])?.focus();
        }
        break;

      case 'Enter':
      case ' ':
        // Toggle submenu on Enter/Space for menu items with submenus
        if (target.matches('[ref="menuitem"]') && target.getAttribute('aria-haspopup') === 'true') {
          event.preventDefault();
          const parentLi = target.closest('.menu-list__list-item');
          if (parentLi) {
            if (this.#state.activeItem === target) {
              this.#deactivate(this.#state.activeItem);
            } else {
              this.activate(event);
            }
          }
        }
        break;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
